import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DollarSign, ShoppingBag, Users, Package, Plus, Trash2, Pencil, X, Search, Download, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { title: "", description: "", category: "Cuidado de la Piel", price: "", compare_at: "", image: "", stock: 50, sold: 0, rating: 4.8, reviews_count: 0, is_flash: false, badge: "" };
const CATS = ["Cuidado de la Piel", "Suplementos", "Fitness", "Recuperación", "Aromaterapia", "Nutrición"];

const ISSUE_LABEL = {
  cj_not_configured: "CJ sin configurar",
  missing_address: "Falta dirección de envío",
  no_shipping_method: "Sin método de envío disponible",
  supplier_error: "Error del proveedor",
};

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [modal, setModal] = useState(null);
  const [cjStatus, setCjStatus] = useState(null);
  const [cjQuery, setCjQuery] = useState("");
  const [cjResults, setCjResults] = useState([]);
  const [cjLoading, setCjLoading] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) nav("/");
    if (user?.role === "admin") loadAll();
    // eslint-disable-next-line
  }, [user, loading]);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/products", { params: { limit: 300 } }).then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/cj/status").then((r) => setCjStatus(r.data)).catch(() => {});
  };

  const cjSearch = async (e) => {
    e?.preventDefault();
    setCjLoading(true);
    try {
      const { data } = await api.get("/admin/cj/search", { params: { keyword: cjQuery } });
      setCjResults(data.items);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCjLoading(false); }
  };

  const cjImportOne = async (pid) => {
    try {
      await api.post("/admin/cj/import", { pid });
      setCjResults((prev) => prev.map((r) => r.pid === pid ? { ...r, imported: true } : r));
      toast.success("Producto importado ✅");
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const bulkImport = async () => {
    const items = bulkText.split(/[\n,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (items.length === 0) { toast.error("Pega al menos un PID o URL de CJ"); return; }
    setBulkBusy(true);
    const totals = { imported: 0, skipped: 0, failed: 0 };
    const chunkSize = 12;
    try {
      for (let i = 0; i < items.length; i += chunkSize) {
        const chunk = items.slice(i, i + chunkSize);
        setBulkProgress({ done: i, total: items.length });
        const { data } = await api.post("/admin/cj/import-pids", { items: chunk });
        totals.imported += data.imported; totals.skipped += data.skipped; totals.failed += data.failed;
      }
      setBulkProgress({ done: items.length, total: items.length });
      toast.success(`✅ ${totals.imported} importados · ${totals.skipped} ya existían · ${totals.failed} fallidos`);
      setBulkText("");
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  const save = async () => {
    const payload = {
      ...modal,
      price: parseFloat(modal.price),
      compare_at: modal.compare_at ? parseFloat(modal.compare_at) : null,
      stock: parseInt(modal.stock) || 0,
      sold: parseInt(modal.sold) || 0,
      reviews_count: parseInt(modal.reviews_count) || 0,
      rating: parseFloat(modal.rating) || 4.8,
      images: [modal.image],
    };
    try {
      if (modal.id) await api.put(`/admin/products/${modal.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Producto guardado");
      setModal(null);
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Eliminado");
    loadAll();
  };

  const fulfillOrder = async (id) => {
    try {
      const { data } = await api.post(`/admin/orders/${id}/fulfill`);
      setOrders((prev) => prev.map((o) => o.id === id ? data : o));
      toast.success(data.fulfillment_status === "processing" ? "Borrador creado en CJ ✅" : `Estado: ${data.fulfillment_status}${data.fulfillment_issue && data.fulfillment_issue !== "none" ? ` (${ISSUE_LABEL[data.fulfillment_issue] || data.fulfillment_issue})` : ""}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="font-display font-black text-3xl uppercase tracking-tight mb-5">Panel Drelo</h1>

      <div className="flex gap-2 mb-6 flex-wrap">
        {[["dashboard", "Resumen"], ["cj", "Importar de CJ"], ["products", "Productos"], ["orders", "Pedidos"]].map(([t, l]) => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-full font-bold text-sm ${tab === t ? "bg-drelo-primary text-white" : "bg-white border"}`} data-testid={`admin-tab-${t}`}>{l}</button>
        ))}
      </div>

      {tab === "dashboard" && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-stats">
          {[
            [DollarSign, `$${stats.revenue.toFixed(2)}`, "Ingresos (pagados)"],
            [ShoppingBag, stats.paid_orders, "Pedidos pagados"],
            [Package, stats.products, "Productos"],
            [Users, stats.customers, "Clientes"],
          ].map(([Icon, v, l], i) => (
            <div key={i} className="bg-white border rounded-2xl p-5">
              <Icon className="w-6 h-6 text-drelo-primary mb-2" />
              <div className="font-display font-black text-3xl">{v}</div>
              <div className="text-xs text-drelo-muted">{l}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "cj" && (
        <div className="space-y-6" data-testid="cj-import-panel">
          <div className="bg-gradient-to-r from-drelo-ink to-[#1e293b] text-white rounded-2xl p-5 flex items-center gap-4">
            <Download className="w-8 h-8 shrink-0" />
            <div className="flex-1">
              <p className="font-bold">CJ Dropshipping conectado {cjStatus?.configured ? "✅" : "⚠️"}</p>
              <p className="text-sm text-white/70">{cjStatus?.cj_products ?? 0} productos importados de CJ. Margen automático x2.5 + precio .99.</p>
            </div>
          </div>

          {/* Bulk import */}
          <div className="bg-white border rounded-2xl p-5">
            <h3 className="font-display font-black text-lg uppercase">Importación masiva (tus 94)</h3>
            <p className="text-sm text-drelo-muted mt-1">Pega los <b>PIDs</b> o <b>URLs</b> de CJ (uno por línea o separados por coma). Los importo y verifico todos: precio, stock e imágenes.</p>
            <textarea
              value={bulkText} onChange={(e) => setBulkText(e.target.value)}
              rows={6} placeholder={"2606221241131605600\nhttps://www.cjdropshipping.com/product/...-p-2606210631011603600.html\n..."}
              className="w-full border rounded-xl px-3 py-2.5 text-sm mt-3 font-mono outline-none focus:border-drelo-primary"
              data-testid="cj-bulk-text"
            />
            {bulkProgress && (
              <div className="mt-2">
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-drelo-primary transition-all" style={{ width: `${(bulkProgress.done / bulkProgress.total) * 100}%` }} /></div>
                <p className="text-xs text-drelo-muted mt-1">{bulkProgress.done}/{bulkProgress.total} procesados</p>
              </div>
            )}
            <button onClick={bulkImport} disabled={bulkBusy} className="mt-3 bg-drelo-primary text-white font-black uppercase px-5 py-3 rounded-xl flex items-center gap-2 disabled:opacity-60" data-testid="cj-bulk-import-btn">
              {bulkBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />} Importar y verificar todos
            </button>
          </div>

          {/* Search import */}
          <div className="bg-white border rounded-2xl p-5">
            <h3 className="font-display font-black text-lg uppercase">Buscador CJ</h3>
            <form onSubmit={cjSearch} className="flex gap-2 mt-3">
              <div className="flex items-center flex-1 border rounded-xl px-3">
                <Search className="w-4 h-4 text-drelo-muted" />
                <input value={cjQuery} onChange={(e) => setCjQuery(e.target.value)} placeholder="Ej: massage gun, led mask, gua sha..." className="px-2 py-2.5 text-sm outline-none w-full" data-testid="cj-search-input" />
              </div>
              <button className="bg-drelo-ink text-white px-5 rounded-xl font-bold text-sm" data-testid="cj-search-btn">Buscar</button>
            </form>
            {cjLoading ? (
              <div className="py-10 text-center text-drelo-muted"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                {cjResults.map((r) => (
                  <div key={r.pid} className="border rounded-xl overflow-hidden" data-testid={`cj-result-${r.pid}`}>
                    <img src={r.image} alt="" className="w-full aspect-square object-cover" />
                    <div className="p-2">
                      <p className="text-xs font-semibold line-clamp-2 min-h-[2rem]">{r.name}</p>
                      <p className="text-[11px] text-drelo-muted mt-1">costo ${r.cost} → <b className="text-drelo-primary">${r.sell}</b></p>
                      <button onClick={() => cjImportOne(r.pid)} disabled={r.imported} className={`mt-2 w-full py-1.5 rounded-lg text-xs font-bold ${r.imported ? "bg-green-100 text-drelo-mint" : "bg-drelo-primary text-white"}`} data-testid={`cj-import-${r.pid}`}>
                        {r.imported ? <span className="flex items-center justify-center gap-1"><Check className="w-3.5 h-3.5" /> Importado</span> : "Importar"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "products" && (
        <div>
          <button onClick={() => setModal({ ...EMPTY })} className="mb-4 bg-drelo-primary text-white font-bold px-4 py-2.5 rounded-xl flex items-center gap-2" data-testid="add-product-btn"><Plus className="w-4 h-4" /> Nuevo producto</button>
          <div className="bg-white border rounded-2xl divide-y">
            {products.map((p) => (
              <div key={p.id} className="flex items-center gap-3 p-3" data-testid={`admin-product-${p.id}`}>
                <img src={p.image} alt={p.title} className="w-12 h-12 rounded-lg object-cover border" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm line-clamp-1">{p.title}</p>
                  <p className="text-xs text-drelo-muted">{p.category} · ${p.price.toFixed(2)} · stock {p.stock}</p>
                </div>
                <button onClick={() => setModal({ ...p, compare_at: p.compare_at || "", badge: p.badge || "" })} className="p-2 text-drelo-muted hover:text-drelo-primary"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(p.id)} className="p-2 text-drelo-muted hover:text-drelo-urgency" data-testid={`del-product-${p.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="bg-white border rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-drelo-muted">
              <tr><th className="p-3">Pedido</th><th className="p-3">Cliente</th><th className="p-3">Método</th><th className="p-3">Pago</th><th className="p-3">Despacho CJ</th><th className="p-3 text-right">Total</th></tr>
            </thead>
            <tbody className="divide-y">
              {orders.map((o) => (
                <tr key={o.id} data-testid={`admin-order-${o.id}`}>
                  <td className="p-3 font-mono">#{o.id.slice(0, 8)}</td>
                  <td className="p-3">{o.email || "invitado"}</td>
                  <td className="p-3">{o.payment_method === "binance" ? "₿ Cripto" : "💳 Tarjeta"}</td>
                  <td className="p-3"><span className={`text-xs font-bold px-2 py-0.5 rounded-full ${o.payment_status === "paid" ? "bg-green-100 text-drelo-mint" : "bg-amber-100 text-amber-700"}`}>{o.payment_status}</span></td>
                  <td className="p-3">
                    {o.fulfillment_status === "fulfilled" ? (
                      <span className="text-xs font-bold text-drelo-mint">✅ Entregado</span>
                    ) : o.fulfillment_status === "processing" ? (
                      <span className="text-xs font-bold text-drelo-ink">📦 Borrador en CJ</span>
                    ) : o.payment_status === "paid" ? (
                      <button onClick={() => fulfillOrder(o.id)} className="text-xs font-bold bg-drelo-ink text-white px-2 py-1 rounded-lg" data-testid={`fulfill-${o.id}`}>
                        {o.fulfillment_status === "failed" ? "Reintentar en CJ" : "Despachar en CJ"}
                      </button>
                    ) : (
                      <span className="text-xs text-drelo-muted">—</span>
                    )}
                    {o.fulfillment_local === "pendiente" && (
                      <div className="text-[10px] font-bold text-amber-700 mt-0.5">📦 Empacar aquí</div>
                    )}
                    {o.fulfillment_issue && o.fulfillment_issue !== "none" && (
                      <div className="text-[10px] text-drelo-urgency mt-0.5">{ISSUE_LABEL[o.fulfillment_issue] || o.fulfillment_issue}</div>
                    )}
                  </td>
                  <td className="p-3 text-right font-black">${o.total.toFixed(2)}</td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-drelo-muted">Sin pedidos todavía</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[90] bg-black/50 grid place-items-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-display font-black text-xl">{modal.id ? "Editar" : "Nuevo"} producto</h3>
              <button onClick={() => setModal(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <input value={modal.title} onChange={(e) => setModal({ ...modal, title: e.target.value })} placeholder="Título" className="w-full border rounded-lg px-3 py-2.5 text-sm" data-testid="prod-title" />
              <textarea value={modal.description} onChange={(e) => setModal({ ...modal, description: e.target.value })} placeholder="Descripción" rows={2} className="w-full border rounded-lg px-3 py-2.5 text-sm" />
              <input value={modal.image} onChange={(e) => setModal({ ...modal, image: e.target.value })} placeholder="URL de imagen" className="w-full border rounded-lg px-3 py-2.5 text-sm" data-testid="prod-image" />
              <div className="grid grid-cols-2 gap-3">
                <select value={modal.category} onChange={(e) => setModal({ ...modal, category: e.target.value })} className="border rounded-lg px-3 py-2.5 text-sm">{CATS.map((c) => <option key={c}>{c}</option>)}</select>
                <input value={modal.badge} onChange={(e) => setModal({ ...modal, badge: e.target.value })} placeholder="Etiqueta (ej. NUEVO)" className="border rounded-lg px-3 py-2.5 text-sm" />
                <input type="number" step="0.01" value={modal.price} onChange={(e) => setModal({ ...modal, price: e.target.value })} placeholder="Precio" className="border rounded-lg px-3 py-2.5 text-sm" data-testid="prod-price" />
                <input type="number" step="0.01" value={modal.compare_at} onChange={(e) => setModal({ ...modal, compare_at: e.target.value })} placeholder="Precio antes" className="border rounded-lg px-3 py-2.5 text-sm" />
                <input type="number" value={modal.stock} onChange={(e) => setModal({ ...modal, stock: e.target.value })} placeholder="Stock" className="border rounded-lg px-3 py-2.5 text-sm" />
                <input type="number" value={modal.sold} onChange={(e) => setModal({ ...modal, sold: e.target.value })} placeholder="Vendidos" className="border rounded-lg px-3 py-2.5 text-sm" />
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={modal.is_flash} onChange={(e) => setModal({ ...modal, is_flash: e.target.checked })} /> Oferta relámpago</label>
              <button onClick={save} className="w-full bg-drelo-primary text-white py-3 rounded-xl font-black uppercase" data-testid="save-product-btn">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
